import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { AlertTriangle, ArrowRight, Keyboard, PencilLine, Sparkles, X } from 'lucide-react'
import { getInputContext, postTransaction, previewTransaction } from '../lib/api'
import { formatIdr } from '../lib/money'
import { jakartaDate } from '../lib/date'
import { parseTransaction } from '../lib/parser'
import type { Account, Category, TransactionDraft } from '../types'
import { Button, Notice, Skeleton } from './Ui'
import { MoneyInput } from './MoneyInput'

function initialDraft(): TransactionDraft {
  return { kind: 'expense', amount_idr: '0', local_date: jakartaDate(), description: '', category_id: null, source_account_id: null, destination_account_id: null, fee_idr: '0', flags: [], confidence: 'review' }
}

export function QuickAdd({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<'text' | 'manual'>('text')
  const [text, setText] = useState('')
  const [parsedText, setParsedText] = useState('')
  const [draft, setDraft] = useState<TransactionDraft>(initialDraft)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [defaultAccountId, setDefaultAccountId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [negative, setNegative] = useState<{ accountId: string; snapshot: string; projected: string } | null>(null)
  const [budgetWarning, setBudgetWarning] = useState<string | null>(null)
  const pendingKey = useRef<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError('')
    setBudgetWarning(null)
    pendingKey.current = null
    void getInputContext()
      .then((context) => {
        setAccounts(context.accounts)
        setCategories(context.categories)
        setDefaultAccountId(context.defaultAccountId)
        setDraft(initialDraft())
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : 'Konteks transaksi tidak dapat dimuat.'))
      .finally(() => setLoading(false))
  }, [open])

  const relevantCategories = useMemo(() => categories.filter((category) => category.kind === (draft.kind === 'income' ? 'income' : 'expense')), [categories, draft.kind])
  const textStale = mode === 'text' && text.trim().length > 0 && text !== parsedText
  const ready = !textStale && BigInt(draft.amount_idr || '0') > 0n && Boolean(draft.description.trim()) && Boolean(draft.local_date) &&
    (draft.kind === 'transfer'
      ? Boolean(draft.source_account_id && draft.destination_account_id && draft.source_account_id !== draft.destination_account_id)
      : Boolean(draft.category_id && (draft.kind === 'income' ? draft.destination_account_id : draft.source_account_id)))
  if (!open) return null

  function editDraft(changes: Partial<TransactionDraft>) {
    pendingKey.current = null
    setDraft((current) => ({ ...current, ...changes, flags: [], confidence: 'review' }))
    setNegative(null)
    setBudgetWarning(null)
  }

  function parseText() {
    pendingKey.current = null
    const parsed = parseTransaction(text, { accounts, categories, defaultAccountId, today: jakartaDate() })
    setDraft(parsed)
    setParsedText(text)
    setNegative(null)
    setBudgetWarning(null)
    setError('')
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (mode === 'text' && text !== parsedText) { setError('Teks berubah. Klik Baca lagi sebelum menyimpan.'); return }
    if (!ready) { setError('Lengkapi nominal, kategori, dompet, dan keterangan sebelum menyimpan.'); return }
    setSaving(true)
    setError('')
    try {
      const preview = await previewTransaction(draft)
      const needsNegativeConsent = preview.would_be_negative && preview.account_id && preview.balance_snapshot_idr && preview.projected_balance_idr && !negative
      const needsBudgetConsent = Boolean(preview.budget_warning) && !budgetWarning
      if (needsNegativeConsent) setNegative({ accountId: preview.account_id!, snapshot: preview.balance_snapshot_idr!, projected: preview.projected_balance_idr! })
      if (needsBudgetConsent) setBudgetWarning(preview.budget_warning)
      if (needsNegativeConsent || needsBudgetConsent) {
        return
      }
      pendingKey.current ??= crypto.randomUUID()
      await postTransaction(draft, pendingKey.current, negative ? { accountId: negative.accountId, balanceSnapshot: negative.snapshot } : undefined)
      pendingKey.current = null
      setSuccess('Transaksi tersimpan dan saldo sudah diperbarui.')
      setText('')
      setParsedText('')
      setDraft(initialDraft())
      setNegative(null)
      setBudgetWarning(null)
      window.dispatchEvent(new CustomEvent('smart-finance:updated'))
      window.setTimeout(() => { setSuccess(''); onClose() }, 900)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Transaksi gagal disimpan.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="dialog-layer" role="presentation">
      <button className="dialog-backdrop" aria-label="Tutup pencatatan" onClick={onClose} />
      <section className="quick-add" role="dialog" aria-modal="true" aria-labelledby="quick-add-title">
        <header><div><span className="eyebrow">Pencatatan baru</span><h2 id="quick-add-title">Catat pergerakan uang</h2></div><button className="icon-button" aria-label="Tutup" onClick={onClose}><X size={20} /></button></header>
        <div className="segmented-control" role="tablist">
          <button role="tab" aria-selected={mode === 'text'} onClick={() => { if (mode !== 'text') pendingKey.current = null; setMode('text') }}><Keyboard size={16} />Tulis langsung</button>
          <button role="tab" aria-selected={mode === 'manual'} onClick={() => { if (mode !== 'manual') { pendingKey.current = null; setDraft(initialDraft()); setParsedText('') } setMode('manual') }}><PencilLine size={16} />Form manual</button>
        </div>
        {loading ? <div className="stack"><Skeleton className="skeleton--field" /><Skeleton className="skeleton--panel" /></div> : (
          <form onSubmit={submit}>
            {mode === 'text' ? <div className="parser-box"><label htmlFor="transaction-text">Tulis seperti biasa</label><div className="parser-input"><input id="transaction-text" value={text} onChange={(event) => { pendingKey.current = null; setText(event.target.value) }} placeholder="Contoh: makan siang 25rb cash" autoFocus /><Button type="button" onClick={parseText} disabled={!text.trim()}><Sparkles size={16} />Baca</Button></div><small>Diproses di perangkat tanpa AI berbayar. Hasil selalu bisa diperbaiki.</small></div> : null}
            <div className="preview-panel">
              <div className="preview-panel__heading"><div><span className="eyebrow">Preview sebelum disimpan</span><h3>Periksa detail transaksi</h3></div><span className={`confidence confidence--${ready ? 'high' : 'invalid'}`}>{ready ? 'Siap disimpan' : 'Belum lengkap'}</span></div>
              {draft.flags.length > 0 ? <div className="flag-list">{draft.flags.map((flag) => <div key={flag.code} className={`flag flag--${flag.severity}`}><AlertTriangle size={15} />{flag.message}</div>)}</div> : null}
              {textStale ? <Notice><span>Teks belum dibaca atau sudah berubah. Klik Baca sebelum menyimpan.</span></Notice> : null}
              {error ? <Notice tone="danger"><span>{error}</span></Notice> : null}
              {success ? <Notice tone="success"><span>{success}</span></Notice> : null}
              <div className="form-grid">
                <label>Jenis<select value={draft.kind} onChange={(event) => { const kind = event.target.value as TransactionDraft['kind']; editDraft({ kind, category_id: null, source_account_id: null, destination_account_id: null }) }}><option value="expense">Pengeluaran</option><option value="income">Pemasukan</option><option value="transfer">Transfer</option></select></label>
                <label>Nominal<MoneyInput value={draft.amount_idr} onValueChange={(value) => editDraft({ amount_idr: value })} placeholder="0" required /><small>{draft.amount_idr !== '0' ? formatIdr(draft.amount_idr) : 'Masukkan rupiah penuh'}</small></label>
                <label>Tanggal<input type="date" value={draft.local_date} onChange={(event) => editDraft({ local_date: event.target.value })} required /></label>
                {draft.kind !== 'transfer' ? <label>Kategori<select value={draft.category_id ?? ''} onChange={(event) => editDraft({ category_id: event.target.value || null })} required><option value="">Pilih kategori</option>{relevantCategories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label> : null}
                {draft.kind !== 'income' ? <label>Dompet sumber<select value={draft.source_account_id ?? ''} onChange={(event) => editDraft({ source_account_id: event.target.value || null })} required><option value="">Pilih dompet</option>{accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</select></label> : null}
                {draft.kind !== 'expense' ? <label>Dompet tujuan<select value={draft.destination_account_id ?? ''} onChange={(event) => editDraft({ destination_account_id: event.target.value || null })} required><option value="">Pilih dompet</option>{accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</select></label> : null}
                {draft.kind === 'transfer' ? <label>Biaya transfer<MoneyInput value={draft.fee_idr} onValueChange={(value) => editDraft({ fee_idr: value })} placeholder="0" /></label> : null}
                <label className="form-grid__full">Keterangan<input value={draft.description} onChange={(event) => editDraft({ description: event.target.value })} placeholder="Keterangan singkat" maxLength={240} required /></label>
              </div>
            </div>
            {budgetWarning ? <Notice><span><strong>Anggaran kategori terlewati.</strong><br />{budgetWarning} Transaksi tetap bisa disimpan jika nominalnya sudah benar.</span></Notice> : null}
            {negative ? <Notice tone="danger"><span><strong>Saldo akan menjadi {formatIdr(negative.projected)}.</strong><br />Klik “Izinkan saldo negatif” untuk mencatat persetujuan berdasarkan saldo terkini {formatIdr(negative.snapshot)}.</span></Notice> : null}
            <footer className="dialog-actions"><Button type="button" variant="ghost" onClick={onClose}>Batal</Button><Button type="submit" loading={saving} disabled={!ready}>{negative && budgetWarning ? 'Izinkan & tetap simpan' : negative ? 'Izinkan saldo negatif' : budgetWarning ? 'Tetap simpan' : 'Simpan transaksi'}<ArrowRight size={16} /></Button></footer>
          </form>
        )}
      </section>
    </div>
  )
}
